/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Contract } from '@ethersproject/contracts'
import { Web3Provider } from '@ethersproject/providers'
import BigNumberJS from 'bignumber.js'

import CouponAbi from '../abi/Coupon.json'
import { COUPON_AMOUNT_POLL_INTERVAL, COUPON_TOKEN_INFO } from '../constants'
import { useWeb3Context } from '../contexts/Web3Context'
import { formatCouponAmountToString } from '../functions/coupons'
import { CouldBe, Nullable } from '../types/main'
import { useDocumentVisibility } from './useDocumentVisibility'

const { address: couponAddress, decimals: couponDecimals } = COUPON_TOKEN_INFO

const getBalanceOfCoupon = async (account: string, library: Web3Provider) => {
  const contract = new Contract(couponAddress, CouponAbi, library)
  const balance = await contract.balanceOf(account)

  if (!balance) {
    throw new Error('Failed to fetch STAMP balance from the chain')
  }

  return new BigNumberJS(balance.toHexString()).div(10 ** couponDecimals)
}

type CouponHookConfig = {
  poll?: boolean
}

const useCouponPolling = (
  config: CouponHookConfig,
  setCouponAmount: Dispatch<SetStateAction<CouldBe<BigNumberJS>>>
) => {
  const { account, library } = useWeb3Context()

  const timerRef = useRef<Nullable<ReturnType<typeof setInterval>>>(null)
  const pollAttemptRef = useRef<number>(0)

  const documentIsVisible = useDocumentVisibility()

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      console.log(`cancel coupon polling with poll id: ${timerRef.current}.`)
      clearInterval(timerRef.current)
      timerRef.current = null
      pollAttemptRef.current = 0
    }
  }, [])

  // Poll with interval timer only when config.poll set to true
  useEffect(() => {
    if (!account || !library || !config.poll) return

    if (!documentIsVisible) {
      resetTimer()
      return
    }

    const updateCouponAmount = async () => {
      pollAttemptRef.current += 1
      const couponAmount = await getBalanceOfCoupon(account, library)
      setCouponAmount(couponAmount)
    }

    try {
      timerRef.current = setInterval(updateCouponAmount, COUPON_AMOUNT_POLL_INTERVAL)
    } catch (e: any) {
      console.error(e)
    }

    return () => {
      resetTimer()
    }
  }, [account, config.poll, documentIsVisible, library, resetTimer, setCouponAmount])
}

export const useCouponAmount = (config?: CouponHookConfig) => {
  const { account, library } = useWeb3Context()

  const [couponAmount, setCouponAmount] = useState<CouldBe<BigNumberJS>>()

  useCouponPolling(config || {}, setCouponAmount)

  const refetch = useCallback(async () => {
    if (!account || !library) return

    try {
      const couponAmount = await getBalanceOfCoupon(account, library)
      setCouponAmount(couponAmount)
    } catch (e: any) {
      console.error(e)
    }
  }, [account, library])

  // Load initial coupon balance when this hook has mounted
  useEffect(() => {
    if (!account || !library) return
    if (couponAmount) return

    let subscribed = true
    console.log('load initial coupon amount')
    ;(async (...args: [string, Web3Provider]) => {
      try {
        const couponAmount = await getBalanceOfCoupon(...args)
        subscribed && setCouponAmount(couponAmount)
      } catch (e: any) {
        console.error(e)
      }
    })(account, library)

    return () => {
      subscribed = false
    }
  }, [account, couponAmount, library])

  return {
    couponAmount: useMemo(() => formatCouponAmountToString(couponAmount), [couponAmount]),
    refetch
  }
}
